import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FaSignOutAlt,
  FaTrashAlt,
  FaUser,
  FaLanguage,
  FaInfoCircle,
  FaEnvelope,
  FaMobileAlt,
} from "react-icons/fa";

import AppHeader from "../common/AppHeader";
import BottomNav from "../common/BottomNav";

import {
  fetchBookingsBySPId,
  deactivateServiceProvider,
  updateNotificationSettings,
  updateServiceProviderLanguage,
} from "../../services/coreService";
import { logout } from "../utils/auth";

import { useTranslation } from "react-i18next";
import styles from "../css/Settings.module.css";

export default function Settings() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [language, setLanguage] = useState(i18n.language || "en");

  const [emailNotificationEnabled, setEmailNotificationEnabled] =
    useState(true);
  const [mobileNotificationEnabled, setMobileNotificationEnabled] =
    useState(true);

  const [showDeactivateConfirm, setShowDeactivateConfirm] =
    useState(false);
  const [loadingDeactivate, setLoadingDeactivate] =
    useState(false);
  const [deactivateError, setDeactivateError] = useState("");
  const [setDeactivateSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const storedUser = JSON.parse(
      localStorage.getItem("user") || "{}"
    );
    setUser(storedUser);
  }, []);

  useEffect(() => {
    const storedLang = localStorage.getItem("lang") || "en";
    i18n.changeLanguage(storedLang);
    setLanguage(storedLang);
  }, [i18n]);

  const handleLanguageChange = async (lng) => {
    setLanguage(lng);
    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);

    if (!user?.spId) {
      return;
    }

    const normalizedLanguage = lng === "hi" ? "hindi" : "english";
    const response = await updateServiceProviderLanguage(user.spId, normalizedLanguage);

    if (response?.responseCode === 200 && Array.isArray(response.responseData) && response.responseData[0]) {
      const updatedUser = {
        ...user,
        ...response.responseData[0],
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } else {
      console.error("Failed to update language preference:", response?.responseMessage || "Unknown error");
    }
  };

  /* ================= LOGOUT ================= */

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  /* ================= DEACTIVATE ================= */

  const handleDeactivateAccount = async () => {
    setLoadingDeactivate(true);
    setDeactivateError("");

    try {
      const userId = user?.spId || "";

      const bookings = await fetchBookingsBySPId(userId);

      if (bookings.length > 0) {
        setDeactivateError(
          t(
            "Settings.cannot_deactivate_with_bookings",
            "Active bookings exist."
          )
        );
        setLoadingDeactivate(false);
        return;
      }

      const response =
        await deactivateServiceProvider(userId);

      if (response.responseCode !== 200) {
        setDeactivateError(
          response.responseMessage ||
            t("Settings.failed_deactivate")
        );
        setLoadingDeactivate(false);
        return;
      }

      setDeactivateSuccess(true);

      setTimeout(() => {
        localStorage.clear();
        navigate("/login");
      }, 1500);
    } catch (err) {
      setDeactivateError(
        err?.message ||
          t("Settings.failed_deactivate")
      );
    }

    setLoadingDeactivate(false);
  };

  /* ================= NOTIFICATIONS ================= */

  const handleEmailToggle = async () => {
    const value = !emailNotificationEnabled;
    setEmailNotificationEnabled(value);

    await updateNotificationSettings(
      user?.spId,
      mobileNotificationEnabled,
      value
    );
  };

  const handleMobileToggle = async () => {
    const value = !mobileNotificationEnabled;
    setMobileNotificationEnabled(value);

    await updateNotificationSettings(
      user?.spId,
      value,
      emailNotificationEnabled
    );
  };

  /* ================= UI ================= */

  return (
    <div className={styles.pageContainer}>
      <AppHeader
        title={t("Settings.settings")}
        showBack
      />

      <div className={styles.contentWrapper}>
        {/* PROFILE */}
        <div className={styles.profileCard}>
          <div className={styles.profileImageWrapper}>
            {user?.profileImage ? (
              <img
                src={user.profileImage}
                className={styles.profileImage}
                alt=""
              />
            ) : (
              <div className={styles.profilePlaceholder}>
                <FaUser size={32} />
              </div>
            )}
          </div>

          <div className={styles.profileInfo}>
            <h3 className={styles.profileName}>{user?.name || "User"}</h3>
            <p className={styles.profileEmail}>{user?.email || ""}</p>
          </div>

          <button
            className={styles.editButton}
            onClick={() => navigate("/profile")}
          >
            {t("Settings.edit_profile", "Edit")}
          </button>
        </div>

        {/* ACCOUNT */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>{t("Settings.account", "Account")}</h4>

          <div className={styles.settingCard}>
            <div
              className={styles.settingRow}
              onClick={() =>
                setShowDeactivateConfirm(true)
              }
            >
              <div className={`${styles.iconWrapper} ${styles.dangerIconWrapper}`}>
                <FaTrashAlt />
              </div>
              <span className={styles.settingLabel}>{t("Settings.deactivate_account", "Deactivate Account")}</span>
            </div>

            <div
              className={styles.settingRow}
              onClick={handleLogout}
            >
              <div className={`${styles.iconWrapper} ${styles.dangerIconWrapper}`}>
                <FaSignOutAlt />
              </div>
              <span className={styles.settingLabel}>{t("Settings.logout", "Logout")}</span>
            </div>
          </div>
        </div>

        {/* PREFERENCES */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>{t("Settings.preferences", "Preferences")}</h4>

          <div className={styles.settingCard}>
            <div className={styles.settingRow}>
              <div className={styles.iconWrapper}>
                <FaLanguage />
              </div>
              <span className={styles.settingLabel}>{t("Settings.language", "Language")}</span>

              <div className={styles.langSelector}>
                <button
                  className={
                    language === "en"
                      ? `${styles.langBtn} ${styles.activeLang}`
                      : styles.langBtn
                  }
                  onClick={() =>
                    handleLanguageChange("en")
                  }
                >
                  EN
                </button>

                <button
                  className={
                    language === "hi"
                      ? `${styles.langBtn} ${styles.activeLang}`
                      : styles.langBtn
                  }
                  onClick={() =>
                    handleLanguageChange("hi")
                  }
                >
                  हिन्दी
                </button>
              </div>
            </div>

            <div className={styles.settingRow}>
              <div className={styles.iconWrapper}>
                <FaEnvelope />
              </div>
              <span className={styles.settingLabel}>{t("Settings.email_notifications", "Email Notifications")}</span>
              
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={emailNotificationEnabled}
                  onChange={handleEmailToggle}
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            <div className={styles.settingRow}>
              <div className={styles.iconWrapper}>
                <FaMobileAlt />
              </div>
              <span className={styles.settingLabel}>{t("Settings.mobile_notifications", "Mobile Notifications")}</span>
              
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={mobileNotificationEnabled}
                  onChange={handleMobileToggle}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>
        </div>

        {/* SUPPORT */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>{t("Settings.support", "Support")}</h4>

          <div className={styles.settingCard}>
            <div
              className={styles.settingRow}
              onClick={() => navigate("/contact")}
            >
              <div className={styles.iconWrapper}>
                <FaEnvelope />
              </div>
              <span className={styles.settingLabel}>{t("Settings.contact_support", "Contact Support")}</span>
            </div>

            <Link
              to="/privacy-policy"
              className={styles.settingRow}
            >
              <div className={styles.iconWrapper}>
                <FaInfoCircle />
              </div>
              <span className={styles.settingLabel}>{t("Settings.privacy_policy", "Privacy Policy")}</span>
            </Link>

            <Link
              to="/terms-and-conditions"
              className={styles.settingRow}
            >
              <div className={styles.iconWrapper}>
                <FaInfoCircle />
              </div>
              <span className={styles.settingLabel}>{t("Settings.terms_conditions", "Terms & Conditions")}</span>
            </Link>
          </div>
        </div>

        <div className={styles.versionInfo}>
          <span>{t("Settings.version", "Version")} 1.0.0</span>
        </div>
      </div>

      {/* MODAL */}
      {showDeactivateConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <p>
              {t(
                "Settings.confirm_deactivate_msg"
              )}
            </p>

            {deactivateError && (
              <p>{deactivateError}</p>
            )}

            <button
              onClick={handleDeactivateAccount}
              disabled={loadingDeactivate}
            >
              {loadingDeactivate
                ? "..."
                : t(
                    "Settings.yes_deactivate"
                  )}
            </button>

            <button
              onClick={() =>
                setShowDeactivateConfirm(false)
              }
            >
              {t("Settings.cancel")}
            </button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}